import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import Modal from '../../components/Modal';
import sarsService from '../../services/sars.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors, gradients } from '../../styles/theme';
import type { Sar, SarStatus } from '../../types/sars';

/**
 * Suspicious Activity Reports queue. A SAR is drafted from a client (on the
 * Client 360 compliance panel); here a SECOND reviewer files it with the
 * regulator reference or rejects it — four-eyes on both (the drafter can neither
 * file nor reject their own). Filed/rejected SARs stay on the record (append-only / WORM).
 */

const STATUS_TONE: Record<SarStatus, BadgeTone> = {
  draft: 'warning',
  filed: 'success',
  rejected: 'danger',
};

interface FileTarget {
  sar: Sar;
  mode: 'file' | 'reject';
}

interface SarActionsProps {
  sar: Sar;
  onFile: () => void;
  onReject: () => void;
}

/** What a row offers: a second reviewer may file or reject a draft; its drafter is told that
 *  someone else does (four-eyes on both); a decided SAR offers nothing. */
function SarActions({ sar, onFile, onReject }: Readonly<SarActionsProps>): JSX.Element {
  if (sar.status !== 'draft') {
    return (
      <span className="text-xs" style={{ color: colors.mutedSoft }}>
        —
      </span>
    );
  }
  if (sar.drafted_by_me) {
    return (
      <span className="text-xs" style={{ color: colors.mutedSoft }}>
        A second reviewer files or rejects it
      </span>
    );
  }
  return (
    <span className="inline-flex gap-2">
      <button
        type="button"
        onClick={onFile}
        className="px-3 py-1.5 rounded-md text-xs font-semibold"
        style={{ background: gradients.gold, color: colors.navy }}
      >
        File
      </button>
      <button
        type="button"
        onClick={onReject}
        className="px-3 py-1.5 rounded-md text-xs font-semibold"
        style={{ border: `1px solid ${colors.border}`, color: colors.inkSoft }}
      >
        Reject
      </button>
    </span>
  );
}

export default function Sars(): JSX.Element {
  const [sars, setSars] = useState<Sar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<FileTarget | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSars(await sarsService.list());
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load SARs.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  let body: JSX.Element;
  if (loading) {
    body = <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>;
  } else if (sars.length === 0) {
    body = (
      <div
        className="rounded-2xl p-8 text-center text-sm"
        style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
      >
        No SARs yet. Draft one from a client&apos;s Compliance controls on their profile.
      </div>
    );
  } else {
    body = (
      <div className="overflow-hidden rounded-2xl" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
        <table className="w-full text-sm">
          <thead style={{ background: colors.canvas, color: colors.muted }}>
            <tr>
              <th className="text-left font-medium px-4 py-3">Reason</th>
              <th className="text-left font-medium px-4 py-3">Status</th>
              <th className="text-left font-medium px-4 py-3">Regulator ref</th>
              <th className="text-left font-medium px-4 py-3">Drafted</th>
              <th className="text-right font-medium px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {sars.map((s, idx) => (
              <tr key={s.id} style={{ borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}` }}>
                <td className="px-4 py-3 max-w-md" style={{ color: colors.ink }}>
                  <span className="line-clamp-2">{s.reason}</span>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge tone={STATUS_TONE[s.status]}>{s.status}</StatusBadge>
                </td>
                <td className="px-4 py-3 font-mono text-xs" style={{ color: colors.muted }}>
                  {s.external_ref ?? '—'}
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: colors.muted }}>
                  {formatDateTime(s.drafted_at)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <SarActions
                    sar={s}
                    onFile={() => setTarget({ sar: s, mode: 'file' })}
                    onReject={() => setTarget({ sar: s, mode: 'reject' })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Suspicious Activity Reports"
        subtitle="A SAR is drafted from a client's signals, then a second reviewer files it with the regulator reference or rejects it — the drafter can do neither on their own report (four-eyes). Filed and rejected reports stay on the record."
      />

      {flash && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.successText }}
        >
          {flash}
        </div>
      )}
      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {error}
        </div>
      )}

      {body}

      {target && (
        <SarDecisionModal
          target={target}
          onClose={() => setTarget(null)}
          onDone={(msg) => {
            setTarget(null);
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 6000);
            reload();
          }}
        />
      )}
    </>
  );
}

interface SarDecisionModalProps {
  target: FileTarget;
  onClose: () => void;
  onDone: (msg: string) => void;
}

function SarDecisionModal({ target, onClose, onDone }: Readonly<SarDecisionModalProps>): JSX.Element {
  const [externalRef, setExternalRef] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isFile = target.mode === 'file';
  const reasonOk = reason.trim().length >= 1;
  const valid = reasonOk && (!isFile || externalRef.trim().length >= 1);
  const ctaLabel = isFile ? 'File SAR' : 'Reject SAR';

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      if (isFile) {
        await sarsService.file(target.sar.id, externalRef.trim(), reason.trim());
        onDone('SAR filed with the regulator reference.');
      } else {
        await sarsService.reject(target.sar.id, reason.trim());
        onDone('SAR rejected.');
      }
    } catch (e: unknown) {
      setErr(getApiErrorMessage(e, 'Could not record the decision.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        {isFile ? 'File SAR' : 'Reject SAR'}
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        {isFile
          ? 'Record the regulator reference. You cannot file a SAR you drafted — a different reviewer must (four-eyes).'
          : 'Rejects the draft without filing. You cannot reject a SAR you drafted — a different reviewer must (four-eyes). The reason is kept on the record.'}
      </p>

      <div className="mb-4 p-3 rounded-lg text-xs" style={{ background: colors.surfaceMuted, color: colors.textPrimary }}>
        <span className="font-semibold">Reason for the SAR: </span>{target.sar.reason}
      </div>

      {isFile && (
        <div className="mb-3">
          <label htmlFor="sar-ref" className="block text-xs font-medium mb-1" style={{ color: colors.muted }}>
            Regulator reference
          </label>
          <input
            id="sar-ref"
            type="text"
            value={externalRef}
            onChange={(e) => setExternalRef(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm font-mono"
            style={{ border: `1px solid ${colors.border}` }}
          />
        </div>
      )}

      <label htmlFor="sar-reason" className="block text-xs font-medium mb-1" style={{ color: colors.muted }}>
        {isFile ? 'Filing note (kept in the audit log)' : 'Reason (kept in the audit log)'}
      </label>
      <textarea
        id="sar-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        className="w-full px-3 py-2 rounded-lg text-sm mb-4"
        style={{ border: `1px solid ${colors.border}` }}
      />

      {err && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {err}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button" onClick={onClose} disabled={busy}
          className="px-4 py-2 rounded-lg text-sm"
          style={{ border: `1px solid ${colors.border}`, color: colors.inkSoft }}
        >
          Cancel
        </button>
        <button
          type="button" onClick={submit} disabled={!valid || busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: gradients.gold, color: colors.navy, opacity: valid && !busy ? 1 : 0.5 }}
        >
          {busy ? 'Saving…' : ctaLabel}
        </button>
      </div>
    </Modal>
  );
}
